import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Switch,
} from "@mui/material";
import { Close, EmailOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import StatusChip from "../common/StatusChip";
import { selectStatusOptions, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";

const StatusUpdateDialog = ({
  open,
  onClose,
  currentStatus,
  onSubmit,
  loading = false,
}) => {
  const statusOptions = useSelector(selectStatusOptions("shipment"));
  const statusOrder = useSelector(selectStatusOrder("shipment"));

  const getNextStatus = () => {
    const currentIdx = statusOrder.indexOf(currentStatus);
    const next = statusOrder[currentIdx + 1];
    return next || "";
  };

  const [status, setStatus] = useState(getNextStatus());
  const [note, setNote] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  useEffect(() => {
    if (open) {
      setStatus(getNextStatus());
      setNote("");
      setNotifyCustomer(true);
    }
  }, [open, currentStatus]);

  const handleSubmit = () => {
    onSubmit(status, note, notifyCustomer);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700}>Update Shipment Status</Typography>
        <IconButton size="small" onClick={handleClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={0.75}
          >
            Current status
          </Typography>
          <StatusChip status={currentStatus} domain="shipment" />
        </Box>

        <TextField
          select
          fullWidth
          label="New Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ mb: 2 }}
        >
          {statusOptions.map((s) => (
            <MenuItem
              key={s.value}
              value={s.value}
              disabled={s.value === currentStatus}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <StatusChip status={s.value} domain="shipment" />
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Internal note (optional)"
          placeholder="e.g. Package scanned at Miami warehouse"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            borderRadius: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <EmailOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Notify customer
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Send an email about this status change
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={notifyCustomer}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
            size="small"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!status || status === currentStatus || loading}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Update Status"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusUpdateDialog;